/**
 * API utilities for evaluation schema operations
 * Handles all evaluation schema-related Firebase calls
 */
import { callFirebaseFunction } from '../firebaseCalls';

/**
 * Create a new evaluation schema
 * @param {Object} schemaData - Schema data object
 * @returns {Promise<any>} Created schema result
 */
export const createEvaluationSchema = async (schemaData: any): Promise<any> => {
  return await callFirebaseFunction('createEvaluationSchema', { schemaData });
};

/**
 * Update an existing evaluation schema
 * @param {string} schemaId - Schema ID
 * @param {Object} schemaData - Updated schema data
 * @returns {Promise<any>} Update result
 */
export const updateEvaluationSchema = async (schemaId: string, schemaData: any): Promise<any> => {
  return await callFirebaseFunction('updateEvaluationSchema', { schemaId, schemaData });
};

/**
 * Delete an evaluation schema
 * @param {string} schemaId - Schema ID to delete
 * @returns {Promise<any>} Delete result
 */
export const deleteEvaluationSchema = async (schemaId: string): Promise<any> => {
  return await callFirebaseFunction('deleteEvaluationSchema', { schemaId });
};

/**
 * Get evaluation schemas for the current user/tenant
 * @param {Object} options - Options object with filters and includeGlobal
 * @param {Object} options.filters - Optional filters (active, category, etc.)
 * @param {boolean} options.includeGlobal - Whether to include global schemas
 * @returns {Promise<any>} List of schemas
 */
export const getEvaluationSchemas = async (options: { filters?: any; includeGlobal?: boolean;[key: string]: any } = {}): Promise<any> => {
  const { filters = {}, includeGlobal = true, ...otherOptions } = options;
  return await callFirebaseFunction('getEvaluationSchemas', {
    filters,
    includeGlobal,
    ...otherOptions
  });
};

/**
 * Get a specific evaluation schema by ID
 * @param {string} schemaId - Schema ID
 * @returns {Promise<any>} Schema data
 */
export const getEvaluationSchema = async (schemaId: string): Promise<any> => {
  return await callFirebaseFunction('getEvaluationSchema', { schemaId });
};

/**
 * Clone a global schema to tenant-specific schema
 * @param {string} globalSchemaId - Global schema ID to clone
 * @param {Object} cloneData - Clone customization data (name, description, etc.)
 * @returns {Promise<any>} Cloned schema result
 */
export const cloneGlobalSchema = async (globalSchemaId: string, cloneData: any): Promise<any> => {
  return await callFirebaseFunction('cloneGlobalSchema', { globalSchemaId, cloneData });
};

/**
 * Set a schema as the default for the tenant
 * @param {string} schemaId - Schema ID to set as default
 * @returns {Promise<any>} Update result
 */
export const setDefaultSchema = async (schemaId: string): Promise<any> => {
  return await callFirebaseFunction('setDefaultSchema', { schemaId });
};

/**
 * Assign a schema to a user
 * @param {string} userId - User ID
 * @param {string} schemaId - Schema ID to assign
 * @returns {Promise<any>} Assignment result
 */
export const assignSchemaToUser = async (userId: string, schemaId: string): Promise<any> => {
  return await callFirebaseFunction('assignSchemaToUser', { userId, schemaId });
};

/**
 * Get global evaluation schemas (templates)
 * @param {Object} filters - Optional filters
 * @returns {Promise<any>} List of global schemas
 */
export const getGlobalEvaluationSchemas = async (filters: any = {}): Promise<any> => {
  return await callFirebaseFunction('getGlobalEvaluationSchemas', { filters });
};

/**
 * Validate schema structure and data
 * @param {Object} schemaData - Schema data to validate
 * @returns {Promise<any>} Validation result
 */
export const validateEvaluationSchema = async (schemaData: any): Promise<any> => {
  return await callFirebaseFunction('validateEvaluationSchema', { schemaData });
};

/**
 * Get schema usage analytics
 * @param {string} schemaId - Schema ID (optional, for specific schema)
 * @returns {Promise<any>} Usage analytics data
 */
export const getSchemaAnalytics = async (schemaId: string | null = null): Promise<any> => {
  return await callFirebaseFunction('getSchemaAnalytics', { schemaId });
};